import React from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendOrder,
	FrontendProductOrder,
	FrontendUserInfo,
} from "node-library/dist/portal";

import { OrderStatus, Severity, UserType } from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { getStatusColors, getStatusString } from "../../../helpers/orderHelper";
import { roleMeetsRequirement } from "node-library/dist/helpers/request_helper";
import {
	AlertIcon,
	COAIcon,
	DeleteIcon,
	EditIcon,
	ViewResultIcon,
} from "../../../common-components/svgIcons";
import { NeutralColor } from "node-library/dist/colors";
import { KebabDropdownButton } from "../../../common-components/button";
import { Card, Tooltip } from "@mui/material";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";

interface Props {
	order: FrontendProductOrder;
	user: FrontendUserInfo | undefined;
	viewOrderCOC: () => void;
	goToViewOrderDetails: () => void;
	editOrder: () => void;
	discardUnstartedOrder: () => void;
	viewResult: () => void;
}

export default function OrderCell(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		order,
		goToViewOrderDetails,
		user,
		discardUnstartedOrder,
		viewOrderCOC,
		viewResult,
		editOrder,
	} = props;

	const statusString: string = getStatusString(order.orderStatus, translation);

	const statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	} = getStatusColors(order.orderStatus);

	let actionOptions: Array<{
		key: string;
		onClick: () => void;
		children: React.ReactNode;
	}>;

	const userIsAdmin: boolean = user
		? roleMeetsRequirement(user?.role, UserType.ENGINEER)
		: false;

	switch (order.orderStatus) {
		case OrderStatus.INCOMPLETE:
			actionOptions = [
				{
					key: `edit-batch-${order.shortId}`,
					onClick: () => {
						editOrder();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
							{translation("Edit")}
						</div>
					),
				},
				{
					key: `delete-order-${order.shortId}`,
					onClick: () => {
						discardUnstartedOrder();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<DeleteIcon />
							{translation("Delete")}
						</div>
					),
				},
			];
			break;
		case OrderStatus.PENDING:
			actionOptions = [
				...(userIsAdmin
					? [
							{
								key: `edit-batch-${order.shortId}`,
								onClick: () => {
									editOrder();
								},
								children: (
									<div className="icon-dropdown-menu-item">
										<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
										{translation("Edit")}
									</div>
								),
							},
					  ]
					: []),
				{
					key: `view-coc-order-${order.shortId}`,
					onClick: () => {
						viewOrderCOC();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<COAIcon />
							{translation("ViewChainOfCustody")}
						</div>
					),
				},
			];
			break;
		case OrderStatus.IN_PROGRESS:
			actionOptions = [
				...(userIsAdmin
					? [
							{
								key: `edit-batch-${order.shortId}`,
								onClick: () => {
									editOrder();
								},
								children: (
									<div className="icon-dropdown-menu-item">
										<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
										{translation("Edit")}
									</div>
								),
							},
							// {
							// 	key: `delete-order-${order.orderBarcodeId}`,
							// 	onClick: () => {
							// 		deleteOrder();
							// 	},
							// 	children: (
							// 		<div className="icon-dropdown-menu-item">
							// 			<DeleteIcon />
							// 			{translation("Delete")}
							// 		</div>
							// 	),
							// },
					  ]
					: []),

				{
					key: `view-coc-order-${order.shortId}`,
					onClick: () => {
						viewOrderCOC();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<COAIcon />
							{translation("ViewChainOfCustody")}
						</div>
					),
				},
				{
					key: `view-result-order-${order.shortId}`,
					onClick: () => {
						viewResult();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<ViewResultIcon />
							{translation("ViewResult")}
						</div>
					),
				},
			];
			break;
		case OrderStatus.SHIPPED:
		// case OrderStatus.READY_TO_SHIP:
		case OrderStatus.DELIVERED:
			actionOptions = [
				...(userIsAdmin
					? [
							{
								key: `edit-batch-${order.shortId}`,
								onClick: () => {
									editOrder();
								},
								children: (
									<div className="icon-dropdown-menu-item">
										<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
										{translation("Edit")}
									</div>
								),
							},
							// {
							// 	key: `delete-order-${order.orderBarcodeId}`,
							// 	onClick: () => {
							// 		deleteOrder();
							// 	},
							// 	children: (
							// 		<div className="icon-dropdown-menu-item">
							// 			<DeleteIcon />
							// 			{translation("Delete")}
							// 		</div>
							// 	),
							// },
					  ]
					: []),

				{
					key: `view-coc-order-${order.shortId}`,
					onClick: () => {
						viewOrderCOC();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<COAIcon />
							{translation("ViewChainOfCustody")}
						</div>
					),
				},
				{
					key: `view-result-order-${order.shortId}`,
					onClick: () => {
						viewResult();
					},
					children: (
						<div className="icon-dropdown-menu-item">
							<ViewResultIcon />
							{translation("ViewResult")}
						</div>
					),
				},
			];
			break;
	}

	const orderHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			<div
				className="typography-caption sample-status-box"
				style={{
					backgroundColor: statusColor.backgroundColor,
					border: `1px solid ${statusColor.borderColor}`,
					color: statusColor.textColor,
				}}
			>
				{statusString}
			</div>

			<KebabDropdownButton
				menuOptions={actionOptions}
				id={`batch-more-action-menu-${order.shortId}`}
				onClick={(e) => {}}
			></KebabDropdownButton>
		</div>
	);

	let licenseInfo: React.ReactNode;
	if (order.orderStatus === OrderStatus.INCOMPLETE) {
		licenseInfo = (
			<Tooltip
				PopperProps={{
					sx: {
						"& .MuiTooltip-tooltip": {
							backgroundColor: NeutralColor.NEUTRAL_GREY_900,
							padding: "12px 16px",
						},
					},
				}}
				title={translation("LicenseDeletedError")}
				arrow
			>
				<span style={{ display: "flex", width: "fit-content" }}>
					<AlertIcon></AlertIcon>
				</span>
			</Tooltip>
		);
	} else {
		licenseInfo = translation("None");
	}
	const orderBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("OrderNumberShort")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.shortId}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("License")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{licenseInfo}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Samples")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.instrumentCount}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Submittedon")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.submitted ? formatCustomDate(order.submitted) : "--"}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Receivedon")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.assigned ? formatCustomDate(order.assigned) : "--"}
				</div>
			</div>
		</div>
	);

	return (
		<Card
			className="card-border-radius-14p card-medium-padding highlight-card"
			style={{ cursor: "pointer" }}
			key={order.shortId}
			onClick={() => {
				goToViewOrderDetails();
			}}
		>
			{orderHeaderRow}
			{orderBody}
		</Card>
	);
}
